import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AccuracyGuidelineChatSidebar from '../components/AccuracyGuidelineChatSidebar';
import {
  aggregateMetricCountByUrl,
  buildAnnotationViewHref,
  displayUrlShort,
  DRILLDOWN_LABELS,
  parseDrilldownFromSearchParams,
  sortUrlMetricRowsByCountDesc,
} from '../services/accuracyCountDrilldown';
import type { AccuracyGuidelineChatContext } from '../services/accuracyGuidelineChat';
import { buildAccuracyReportDetailPath } from '../services/accuracyReportPaths';
import { buildTuningRunDetailPath } from '../services/accuracyTuningPaths';
import {
  getBaselineErrors as getBaselineErrorsApi,
  getBaselineReport as getBaselineReportApi,
  getEvaluationRun as getEvaluationRunApi,
  getEvaluationRunErrors as getEvaluationRunErrorsApi,
  getMonitoringJobErrors as getMonitoringJobErrorsApi,
  getTuningSession as getTuningSessionApi,
} from '../services/accuracyBackendApi';
import type { AccuracyErrorItem } from '../types/accuracy';
import './AccuracyCountDrilldownPage.css';
import './AccuracyTuningPages.css';

const AccuracyCountDrilldownPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const parsed = useMemo(() => parseDrilldownFromSearchParams(searchParams), [searchParams]);

  const isTuning = Boolean(parsed?.evaluationRunId && parsed?.tuningSessionId);

  const [apiReport, setApiReport] = useState<any>(undefined);
  const [apiTuningSession, setApiTuningSession] = useState<any>(undefined);
  const [apiTuningRun, setApiTuningRun] = useState<any>(undefined);
  const [apiErrors, setApiErrors] = useState<AccuracyErrorItem[]>([]);

  useEffect(() => {
    if (!parsed) return;
    (async () => {
      try {
        if (parsed.reportId) {
          setApiReport(await getBaselineReportApi(parsed.reportId));
          setApiErrors(await getBaselineErrorsApi(parsed.reportId));
        } else if (parsed.evaluationRunId && parsed.tuningSessionId) {
          setApiTuningSession(await getTuningSessionApi(parsed.tuningSessionId));
          setApiTuningRun(await getEvaluationRunApi(parsed.evaluationRunId));
          setApiErrors(await getEvaluationRunErrorsApi(parsed.evaluationRunId));
        } else if (parsed.jobId) {
          setApiErrors(await getMonitoringJobErrorsApi(parsed.jobId));
        }
      } catch {
        setApiErrors([]);
      }
    })();
  }, [parsed]);

  const report = useMemo(
    () => {
      if (!parsed?.reportId) return undefined;
      return apiReport;
    },
    [parsed, apiReport]
  );

  const tuningSession = useMemo(
    () => {
      if (!parsed?.tuningSessionId) return undefined;
      return apiTuningSession;
    },
    [parsed, apiTuningSession]
  );

  const tuningRun = useMemo(
    () => {
      if (!parsed?.evaluationRunId) return undefined;
      return apiTuningRun;
    },
    [parsed, apiTuningRun]
  );

  const errors = useMemo((): AccuracyErrorItem[] => {
    if (!parsed) return [];
    return apiErrors;
  }, [parsed, apiErrors]);

  const rows = useMemo(() => {
    if (!parsed) return [];
    const { reportId: _r, evaluationRunId: _e, tuningSessionId: _t, ...drilldown } = parsed;
    return sortUrlMetricRowsByCountDesc(aggregateMetricCountByUrl(errors, drilldown));
  }, [parsed, errors]);

  const totalCount = useMemo(() => rows.reduce((sum, r) => sum + r.count, 0), [rows]);

  const sampleErrors = useMemo((): AccuracyErrorItem[] => {
    if (!parsed) return [];
    const { reportId: _r, evaluationRunId: _e, tuningSessionId: _t, ...drilldown } = parsed;
    const errorKind =
      drilldown.countKind === 'wb' ? 'wrong_bounding_box' : drilldown.countKind;
    return errors
      .filter(
        (e) =>
          e.type === drilldown.metricType &&
          e.error === errorKind &&
          (drilldown.categoryId == null || e.category_id === drilldown.categoryId)
      )
      .slice(0, 20);
  }, [parsed, errors]);

  const chatContext = useMemo((): AccuracyGuidelineChatContext | null => {
    if (!parsed || parsed.jobId) return null;
    const { reportId, ...drilldown } = parsed;
    return {
      reportId: reportId || parsed.tuningSessionId || '',
      reportName: isTuning
        ? `${tuningSession?.name || ''} · 評価 #${tuningRun?.run_number ?? ''}`
        : report?.name,
      guidelineVersion: isTuning
        ? tuningSession?.base_guideline_version ?? 0
        : report?.filters.guideline_version ?? 0,
      drilldown,
      totalItemCount: totalCount,
      urlCount: rows.length,
      sampleErrors,
    };
  }, [parsed, report, tuningSession, tuningRun, isTuning, totalCount, rows.length, sampleErrors]);

  const backHref = useMemo(() => {
    if (!parsed) return '/accuracy';
    if (parsed.tuningSessionId && parsed.evaluationRunId) {
      return buildTuningRunDetailPath(parsed.tuningSessionId, parsed.evaluationRunId);
    }
    if (parsed.reportId) {
      return buildAccuracyReportDetailPath(parsed.reportId);
    }
    if (parsed.jobId) {
      return '/accuracy/monitoring';
    }
    return '/accuracy';
  }, [parsed]);

  if (!parsed) {
    return (
      <div className="accuracy-page accuracy-drilldown-page">
        <Link to="/accuracy" className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          評価レポート
        </Link>
        <div className="accuracy-card accuracy-drilldown-empty">表示条件が不正です。</div>
      </div>
    );
  }

  const title = [
    parsed.categoryName,
    DRILLDOWN_LABELS.metricType[parsed.metricType],
    DRILLDOWN_LABELS.countKind[parsed.countKind],
  ].join(' · ');

  const returnTo = `${location.pathname}${location.search}`;

  return (
    <div className="accuracy-drilldown-page-shell">
      <div className="accuracy-drilldown-page-main">
        <Link to={backHref} className="accuracy-drilldown-back">
          <ArrowLeft size={16} />
          {isTuning ? '評価の詳細' : parsed.jobId ? '精度モニタリング' : '評価レポート'}
        </Link>

        <header className="accuracy-drilldown-page-header">
          <h1>{title}</h1>
          {isTuning && tuningSession && (
            <p className="accuracy-drilldown-report-name">
              <span className="accuracy-flow-badge tuning">チューニング</span>{' '}
              {tuningSession.name}
              {tuningRun ? ` · 評価 #${tuningRun.run_number}` : ''}
            </p>
          )}
          {!isTuning && report && <p className="accuracy-drilldown-report-name">{report.name}</p>}
          {parsed.jobId && (
            <p className="accuracy-drilldown-report-name">{parsed.jobName || parsed.jobId}</p>
          )}
          <p className="accuracy-drilldown-meta">
            {totalCount} 件 · {rows.length} URL
          </p>
        </header>

        <div className="accuracy-card accuracy-drilldown-table-card">
          <table className="accuracy-table accuracy-drilldown-table">
            <thead>
              <tr>
                <th>件数</th>
                <th>ジョブ</th>
                <th>URL</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="accuracy-empty">
                    該当なし
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={`${row.job_id}:${row.url_id}`}>
                  <td>{row.count}</td>
                  <td>{row.job_name || row.job_id}</td>
                  <td className="accuracy-drilldown-url" title={row.url}>
                    {displayUrlShort(row.url)}
                  </td>
                  <td>
                    {(() => {
                      const { reportId: _r, evaluationRunId: _e, tuningSessionId: _t, ...drilldown } =
                        parsed;
                      const errorKind =
                        drilldown.countKind === 'wb' ? 'wrong_bounding_box' : drilldown.countKind;
                      const rowErrors = errors.filter(
                        (e) =>
                          e.job_id === row.job_id &&
                          e.url_id === row.url_id &&
                          e.type === drilldown.metricType &&
                          e.error === errorKind &&
                          (drilldown.categoryId == null || e.category_id === drilldown.categoryId)
                      );
                      const href = buildAnnotationViewHref({
                        row,
                        rowErrors,
                        drilldown,
                        returnTo,
                      });
                      return (
                        <Link to={href} className="accuracy-link">
                          View
                        </Link>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {chatContext && <AccuracyGuidelineChatSidebar context={chatContext} />}
    </div>
  );
};

export default AccuracyCountDrilldownPage;
